import { useState } from "react";
import { Link } from "react-router-dom";
import { ShieldCheck } from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import PasswordInput from "@/components/PasswordInput";
import { Label } from "@/components/ui/label";
import Logo from "@/components/Logo";
import { toast } from "sonner";

export default function ForgotPassword() {
  const [email, setEmail] = useState("");
  const [token, setToken] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(false);

  const requestToken = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const { data } = await api.post("/auth/forgot-password", { email });
      if (data.reset_token) {
        setToken(data.reset_token);
        toast.success("Reset token generated (demo mode)");
        setStep(2);
      } else {
        toast.success("If an account exists, a reset link has been sent.");
      }
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setLoading(false);
    }
  };

  const reset = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await api.post("/auth/reset-password", { token, password: newPassword });
      toast.success("Password reset successful. You can sign in now.");
      setStep(3);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-slate-50 p-6">
      <div className="max-w-md w-full bg-white rounded-xl border border-slate-200 shadow-sm p-8">
        <Link to="/" className="flex items-center mb-8">
          <Logo className="h-8" />
        </Link>

        {step === 1 && (
          <>
            <h1 className="text-2xl font-bold tracking-tight">Reset your password</h1>
            <p className="mt-2 text-sm text-slate-500">
              Enter your email and we'll generate a reset token.
            </p>
            <form onSubmit={requestToken} className="mt-6 space-y-4">
              <div>
                <Label className="text-sm font-medium text-slate-700">Email</Label>
                <Input
                  type="email"
                  value={email}
                  required
                  onChange={(e) => setEmail(e.target.value)}
                  data-testid="forgot-email-input"
                  className="mt-1.5 h-11"
                />
              </div>
              <Button
                disabled={loading}
                className="w-full h-11 bg-emerald-600 hover:bg-emerald-700"
                data-testid="forgot-submit-btn"
              >
                {loading ? "Generating…" : "Send reset link"}
              </Button>
            </form>
          </>
        )}

        {step === 2 && (
          <>
            <h1 className="text-2xl font-bold tracking-tight">Set a new password</h1>
            <p className="mt-2 text-sm text-slate-500">
              Demo mode: your reset token is prefilled below.
            </p>
            <form onSubmit={reset} className="mt-6 space-y-4">
              <div>
                <Label className="text-sm font-medium text-slate-700">Reset token</Label>
                <Input value={token} onChange={(e) => setToken(e.target.value)} className="mt-1.5 h-11 font-mono text-xs" />
              </div>
              <div>
                <Label className="text-sm font-medium text-slate-700">New password</Label>
                <PasswordInput
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  required
                  className="mt-1.5 h-11"
                  data-testid="reset-password-input"
                  testIdToggle="reset-password-toggle"
                />
              </div>
              <Button
                disabled={loading}
                className="w-full h-11 bg-emerald-600 hover:bg-emerald-700"
                data-testid="reset-submit-btn"
              >
                {loading ? "Resetting…" : "Reset password"}
              </Button>
            </form>
          </>
        )}

        {step === 3 && (
          <div className="text-center py-6">
            <div className="mx-auto h-12 w-12 rounded-full bg-emerald-50 border border-emerald-200 flex items-center justify-center">
              <ShieldCheck className="h-6 w-6 text-emerald-600" />
            </div>
            <h1 className="mt-4 text-2xl font-bold tracking-tight">All set</h1>
            <p className="mt-2 text-sm text-slate-500">Your password has been updated.</p>
            <Link to="/login">
              <Button className="mt-6 w-full h-11 bg-emerald-600 hover:bg-emerald-700">
                Back to sign in
              </Button>
            </Link>
          </div>
        )}

        {step !== 3 && (
          <div className="mt-6 text-center text-sm text-slate-500">
            Remembered it?{" "}
            <Link to="/login" className="font-medium text-slate-900 hover:text-emerald-700">
              Sign in
            </Link>
          </div>
        )}
      </div>
    </div>
  );
}
